import React from "react";
import {
  Typography,
  Dialog,
  DialogContent,
  Button,
  DialogActions,
  styled,
  DialogTitle,
} from "@mui/material";

// Same 24px side padding as the title and the text, so the buttons line up
// with them instead of reaching the dialog's edge.
const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "8px 24px 24px",
  gap: "8px",
  flexDirection: "column-reverse",
  alignItems: "center",
  [theme.breakpoints.up("sm")]: {
    gap: "16px",
    flexDirection: "row",
    alignItems: "end",
  },
}));

// Wide enough for two buttons side by side, narrow enough that a one-line
// notice does not leave the dialog mostly empty.
const StyledDialog = styled(Dialog)({
  ".MuiDialog-paper": {
    maxWidth: "480px",
    borderRadius: "8px",
    width: "100%",
  },
});

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "100px",
  maxWidth: "290px",
  textTransform: "none",
  "&:hover, &:focus": {
    cursor: "pointer",
  },
  [theme.breakpoints.up("sm")]: {
    padding: "9px 24px",
  },
  gap: "6px",
}));

export interface ActionModalButtonProps {
  label: string;
  onClick: () => void;
}

export interface ActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: React.ReactNode;
  primaryButton: ActionModalButtonProps;
  /** Left out for a notice that has only one way forward. */
  secondaryButton?: ActionModalButtonProps;
}

export const ActionModal: React.FC<ActionModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  primaryButton,
  secondaryButton,
}) => {
  return (
    <StyledDialog open={isOpen} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      {description ? (
        <DialogContent>
          {/* Regular weight, so the words a description sets in bold stand out. */}
          <Typography variant="body1" component="div">
            {description}
          </Typography>
        </DialogContent>
      ) : null}
      <StyledDialogActions>
        {secondaryButton && (
          <StyledButton onClick={secondaryButton.onClick} variant="text" color="primary">
            {secondaryButton.label}
          </StyledButton>
        )}
        <StyledButton variant="contained" onClick={primaryButton.onClick}>
          {primaryButton.label}
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};
